import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatCurrency, API_URL } from "../../lib/utils";
import { Ban, KeyRound } from "lucide-react";

export const InvoiceCancelDialog = ({ invoice, open, onClose, headers, onDone }) => {
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Indique o motivo da anulação");
    if (code.replace(/\D/g, "").length !== 6) return toast.error("O código de anulação tem 6 dígitos");
    setBusy(true);
    try {
      const params = new URLSearchParams({ reason: reason.trim(), code: code.replace(/\D/g, "") });
      const res = await axios.put(`${API_URL}/invoices/${invoice.invoice_id}/cancel?${params}`, {}, { headers, withCredentials: true });
      const restored = res.data.stock_restored_items || [];
      const reversals = res.data.bank_reversals || [];
      toast.success(`Fatura ${invoice.invoice_number} anulada${restored.length ? ` — stock reposto: ${restored.join(", ")}` : ""}${reversals.length ? ` — estorno TPA no banco: ${reversals.map((r) => `${r.tpa_name} ${formatCurrency(r.amount)}`).join(", ")}` : ""}`, { duration: 8000 });
      setReason("");
      setCode("");
      onDone?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao anular fatura");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cancel-invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><Ban className="w-5 h-5" /> Anular {invoice?.invoice_number}</DialogTitle>
          <DialogDescription>
            Documento no valor de <strong className="font-mono">{formatCurrency(invoice?.total)}</strong>. A anulação repõe automaticamente o stock dos artigos, cancela o pedido associado e fica registada na auditoria com o seu nome.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Motivo da anulação *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: fatura emitida em duplicado, erro no cliente" data-testid="cancel-invoice-reason-input" />
          </div>
          <div className="space-y-1">
            <Label className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Código de anulação *</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              className="font-mono text-lg tracking-[0.4em] text-center"
              data-testid="cancel-invoice-code-input"
            />
            <p className="text-xs text-muted-foreground">Código de 6 dígitos gerado pelo administrador da empresa ou pelo suporte LUMORA. Uso único, válido 24h.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Voltar</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="confirm-cancel-invoice-btn">
            <Ban className="w-4 h-4 mr-2" /> Anular Fatura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
